import React, { useEffect, useMemo, useState } from "react";
import { Gauge, Utensils } from "lucide-react";
import { AppCard } from "@/components/daily/CompanionAppUi";
import { evaluateBodyMassGoal } from "@/components/daily/bodyMassEngine";
import { loadRoutineSettings } from "@/components/daily/routineSettings";

const numberOrNull = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const macroCalories = (protein, carbs, fat) => {
  const values = [protein, carbs, fat];
  if (!values.some(Number.isFinite)) return null;
  return Math.round((protein || 0) * 4 + (carbs || 0) * 4 + (fat || 0) * 9);
};

const CalendarMacroTotals = ({ identity }) => {
  const [settings, setSettings] = useState(() => loadRoutineSettings());

  useEffect(() => {
    const refresh = () => setSettings(loadRoutineSettings());
    window.addEventListener("companion-routine-settings-saved", refresh);
    window.addEventListener("apos-cloud-data-hydrated", refresh);
    return () => {
      window.removeEventListener("companion-routine-settings-saved", refresh);
      window.removeEventListener("apos-cloud-data-hydrated", refresh);
    };
  }, []);

  const profile = settings?.bodyMassNutrition || null;
  const result = useMemo(
    () => (profile ? evaluateBodyMassGoal(profile, identity || {}) : null),
    [identity, profile],
  );

  const totals = useMemo(() => {
    if (!profile?.enabled) return null;

    const protein =
      numberOrNull(profile.targetProtein) ||
      numberOrNull(result?.selectedMacros?.protein) ||
      numberOrNull(profile.currentProtein);
    const carbs =
      numberOrNull(profile.targetCarbs) ||
      numberOrNull(result?.selectedMacros?.carbs) ||
      numberOrNull(profile.currentCarbs);
    const fat =
      numberOrNull(profile.targetFat) ||
      numberOrNull(result?.selectedMacros?.fat) ||
      numberOrNull(profile.currentFat);
    const calories =
      numberOrNull(result?.workingCalories) ||
      numberOrNull(profile.currentCalories) ||
      numberOrNull(result?.selectedMacroCalories) ||
      macroCalories(protein, carbs, fat);

    return {
      calories,
      protein,
      carbs,
      fat,
      source:
        result?.ready && result?.selectedMacros
          ? "Working daily target"
          : "Saved daily intake",
    };
  }, [profile, result]);

  return (
    <AppCard className="mt-4" tone="information" data-testid="calendar-macro-totals">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="forr-overline">Calendar nutrition context</div>
          <h3 className="mt-2 font-display text-2xl forr-text-ink">Daily macro totals</h3>
          <p className="mt-2 text-xs leading-relaxed forr-text-mute">
            These totals sit with your schedule so training load, recovery demands, and nutrition planning can be viewed together.
          </p>
        </div>
        <Utensils size={22} className="shrink-0 forr-text-teal-deep" />
      </div>

      {!profile?.enabled ? (
        <div className="mt-4 rounded-2xl p-4 forr-surface-tint">
          <p className="text-sm leading-relaxed forr-text-soft">
            Enable Body-mass & nutrition support below the calendar to add daily calories and macro totals here.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ["Calories", totals?.calories, "kcal"],
              ["Protein", totals?.protein, "g"],
              ["Carbohydrate", totals?.carbs, "g"],
              ["Fat", totals?.fat, "g"],
            ].map(([label, value, unit]) => (
              <div key={label} className="rounded-2xl p-3 forr-surface-tint">
                <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.12em] forr-text-mute">
                  {label === "Calories" && <Gauge size={12} />}
                  {label}
                </div>
                <div className="mt-2 font-display text-2xl forr-text-ink">
                  {Number.isFinite(value) ? value : "—"}
                  {Number.isFinite(value) && (
                    <span className="ml-1 text-xs font-sans forr-text-mute">{unit}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-[0.14em] forr-text-mute">
            {totals?.source || "Saved nutrition plan"}
          </p>
        </>
      )}
    </AppCard>
  );
};

export default CalendarMacroTotals;
